import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import { RefreshCw, Calendar, ShoppingCart, Package, BarChart2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { currencySymbol } from '../../utils/currencySymbol';
import ShopifySyncStatus from '../../components/ShopifySyncStatus';

const formatCurrency = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function toYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMonthLabel(ymd) {
  try {
    const d = new Date(ymd);
    return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return ymd;
  }
}

function formatShortNumber(value) {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = abs < 10_000_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = abs < 10_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}K`;
  }
  return n.toString();
}

function computeIsoWeekParts(ymd) {
  const d = new Date(ymd + 'T00:00:00');
  const isoDow = ((d.getDay() + 6) % 7) + 1;
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + (4 - isoDow));
  const isoYear = thursday.getFullYear();
  const jan1 = new Date(isoYear, 0, 1);
  const jan1IsoDow = ((jan1.getDay() + 6) % 7) + 1;
  const firstThursday = new Date(jan1);
  firstThursday.setDate(jan1.getDate() + (4 - jan1IsoDow));
  const diffDays = Math.floor((thursday - firstThursday) / 86400000);
  const isoWeek = Math.floor(diffDays / 7) + 1;
  return { isoYear, isoWeek, isoDow };
}

export default function ShopifyOverview() {
  const { selectedBrand } = useAuth();
  const CUR = currencySymbol(selectedBrand?.currency);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return toYmdLocal(d);
  });
  const [endDate, setEndDate] = useState(() => toYmdLocal(new Date()));
  const [metric, setMetric] = useState('revenue');
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [dailyRows, setDailyRows] = useState([]);
  const [channelRows, setChannelRows] = useState([]);
  const [dailyByChannelRows, setDailyByChannelRows] = useState([]);
  const [monthlyByChannelAll, setMonthlyByChannelAll] = useState([]);
  const [channelMetric, setChannelMetric] = useState('revenue');
  const [weekdayByWeek, setWeekdayByWeek] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [weeklyMetric, setWeeklyMetric] = useState('revenue');
  const [dailyChannelRowsAll, setDailyChannelRowsAll] = useState([]);
  const [weekdayChannel, setWeekdayChannel] = useState('');
  const [dailyViewAllRows, setDailyViewAllRows] = useState([]);

  const lineData = useMemo(() => {
    return (dailyRows || []).map(r => {
      const label = String(r.day || r.date || r.order_date || '').slice(0, 10);
      const value =
        metric === 'revenue'
          ? Number(r.revenue || 0)
          : metric === 'orders'
          ? Number(r.orders_count || 0)
          : Number(r.items_count || 0);
      return { label, value };
    });
  }, [dailyRows, metric]);

  const totalRevenue = (dailyRows || []).reduce((a, r) => a + Number(r.revenue || 0), 0);
  const totalOrders = (dailyRows || []).reduce((a, r) => a + Number(r.orders_count || 0), 0);
  const totalItems = (dailyRows || []).reduce((a, r) => a + Number(r.items_count || 0), 0);
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  const channelSeries = useMemo(() => {
    const rows = dailyByChannelRows || [];
    if (rows.length === 0) return { labels: [], datasets: [] };
    const labelSet = new Set(rows.map(r => String(r.day).slice(0, 10)));
    const labels = Array.from(labelSet).sort();
    const totalsByChannel = {};
    for (const r of rows) {
      const name = r.sales_channel_name || 'Unknown';
      if (!totalsByChannel[name]) totalsByChannel[name] = { revenue: 0, orders: 0, items: 0 };
      totalsByChannel[name].revenue += Number(r.revenue || 0);
      totalsByChannel[name].orders += Number(r.orders_count || 0);
      totalsByChannel[name].items += Number(r.items_count || 0);
    }
    const rankKey = metric === 'revenue' ? 'revenue' : metric === 'orders' ? 'orders' : 'items';
    const topChannels = Object.keys(totalsByChannel)
      .sort((a, b) => totalsByChannel[b][rankKey] - totalsByChannel[a][rankKey])
      .slice(0, 4);
    const palette = [
      { stroke: 'rgb(37, 99, 235)', fill: 'rgba(37, 99, 235, 0.10)' },
      { stroke: 'rgb(16, 185, 129)', fill: 'rgba(16, 185, 129, 0.10)' },
      { stroke: 'rgb(139, 92, 246)', fill: 'rgba(139, 92, 246, 0.10)' },
      { stroke: 'rgb(234, 88, 12)', fill: 'rgba(234, 88, 12, 0.10)' }
    ];
    const datasets = topChannels.map((ch, idx) => {
      const perDay = new Map(
        rows
          .filter(r => (r.sales_channel_name || 'Unknown') === ch)
          .map(r => [String(r.day).slice(0, 10),
            metric === 'revenue' ? Number(r.revenue || 0)
            : metric === 'orders' ? Number(r.orders_count || 0)
            : Number(r.items_count || 0)
          ])
      );
      return {
        label: ch,
        data: labels.map(d => perDay.get(d) ?? 0),
        borderColor: palette[idx % palette.length].stroke,
        backgroundColor: palette[idx % palette.length].fill,
        fill: true,
        tension: 0.4
      };
    });
    return { labels, datasets };
  }, [dailyByChannelRows, metric]);

  const channelPivot = useMemo(() => {
    const rows = monthlyByChannelAll || [];
    if (rows.length === 0) return { months: [], channels: [], valuesByChannel: {} };
    const monthSet = new Set(rows.map(r => String(r.month).slice(0, 10)));
    const months = Array.from(monthSet).sort();
    const totals = {};
    const valuesByChannel = {};
    for (const r of rows) {
      const ch = r.sales_channel_name || 'Unknown';
      const m = String(r.month).slice(0, 10);
      const val = channelMetric === 'revenue'
        ? Number(r.revenue || 0)
        : channelMetric === 'orders'
        ? Number(r.orders_count || 0)
        : Number(r.items_count || 0);
      if (!valuesByChannel[ch]) valuesByChannel[ch] = {};
      valuesByChannel[ch][m] = (valuesByChannel[ch][m] || 0) + val;
      totals[ch] = (totals[ch] || 0) + val;
    }
    const channels = Object.keys(valuesByChannel).sort((a, b) => (totals[b] || 0) - (totals[a] || 0) || a.localeCompare(b));
    return { months, channels, valuesByChannel };
  }, [monthlyByChannelAll, channelMetric]);

  const fetchData = async (overrideStart, overrideEnd) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.warn('No brand selected');
        setDailyRows([]); setChannelRows([]); setDailyByChannelRows([]);
        return;
      }
      setLoading(true);
      const params = new URLSearchParams();
      const s = overrideStart || startDate;
      const e = overrideEnd || endDate;
      if (s) params.set('startDate', s);
      if (e) params.set('endDate', e);
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      // Daily series (from view endpoint)
      const dailyRes = await fetch(`${base}/api/shopify/orders/daily-view?${params.toString()}`, { credentials: 'include', headers });
      const dailyJs = await dailyRes.json().catch(() => ({}));
      setDailyRows(dailyRes.ok && dailyJs?.rows ? dailyJs.rows : []);
      // Channel monthly -> reduce to totals
      const chanRes = await fetch(`${base}/api/shopify/orders/monthly-by-channel?${params.toString()}`, { credentials: 'include', headers });
      const chanJs = await chanRes.json().catch(() => ({}));
      const monthly = chanRes.ok && chanJs?.rows ? chanJs.rows : [];
      const agg = {};
      for (const r of monthly) {
        const k = r.sales_channel_name || 'Unknown';
        if (!agg[k]) agg[k] = { channel: k, orders: 0, items: 0, revenue: 0 };
        agg[k].orders += Number(r.orders_count || 0);
        agg[k].items += Number(r.items_count || r.items || 0);
        agg[k].revenue += Number(r.revenue || 0);
      }
      setChannelRows(Object.values(agg));
      // Daily by channel
      const dailyChanRes = await fetch(`${base}/api/shopify/orders/daily-by-channel?${params.toString()}`, { credentials: 'include', headers });
      const dailyChanJs = await dailyChanRes.json().catch(() => ({}));
      setDailyByChannelRows(dailyChanRes.ok && dailyChanJs?.rows ? dailyChanJs.rows : []);
      // All-time monthly by channel (no date filters)
      const allMonthlyRes = await fetch(`${base}/api/shopify/orders/monthly-by-channel`, { credentials: 'include', headers });
      const allMonthlyJs = await allMonthlyRes.json().catch(() => ({}));
      const allRows = allMonthlyRes.ok && allMonthlyJs?.rows ? allMonthlyJs.rows : [];
      allRows.sort((a, b) => String(a.month).localeCompare(String(b.month)) || String(a.sales_channel_name || '').localeCompare(String(b.sales_channel_name || '')));
      setMonthlyByChannelAll(allRows);
      // Weekday by ISO week
      let wdwRes = await fetch(`${base}/api/shopify/orders/weekday-by-iso-week?${params.toString()}`, { credentials: 'include', headers });
      let wdwJs = await wdwRes.json().catch(() => ({}));
      let wdwRows = wdwRes.ok && wdwJs?.rows ? wdwJs.rows : [];
      if (!Array.isArray(wdwRows) || wdwRows.length === 0) {
        wdwRes = await fetch(`${base}/api/shopify/orders/weekday-by-iso-week`, { credentials: 'include', headers });
        wdwJs = await wdwRes.json().catch(() => ({}));
        wdwRows = wdwRes.ok && wdwJs?.rows ? wdwJs.rows : [];
      }
      setWeekdayByWeek(wdwRows);
      // Daily channel view (selected range)
      const dcvRes = await fetch(`${base}/api/shopify/orders/daily-by-channel-view?${params.toString()}`, { credentials: 'include', headers });
      const dcvJs = await dcvRes.json().catch(() => ({}));
      // No need to put into separate state; weekly tables use all-time below
      // All-time daily-by-channel view
      const dcvAllRes = await fetch(`${base}/api/shopify/orders/daily-by-channel-view`, { credentials: 'include', headers });
      const dcvAllJs = await dcvAllRes.json().catch(() => ({}));
      setDailyChannelRowsAll(dcvAllRes.ok && dcvAllJs?.rows ? dcvAllJs.rows : []);
      // All-time daily view (aggregate over channels) for weekday/WoW tables
      const dvAllRes = await fetch(`${base}/api/shopify/orders/daily-view`, { credentials: 'include', headers });
      const dvAllJs = await dvAllRes.json().catch(() => ({}));
      setDailyViewAllRows(dvAllRes.ok && dvAllJs?.rows ? dvAllJs.rows : []);
    } catch (e) {
      console.error('SHOPIFY fetch error', e);
      setDailyRows([]); setChannelRows([]); setDailyByChannelRows([]); setMonthlyByChannelAll([]); setWeekdayByWeek([]); setDailyChannelRowsAll([]); setWeeklySummary([]);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  const applyQuickRange = (id) => {
    const today = new Date();
    let s = new Date(today);
    let e = new Date(today);
    switch (id) {
      case 'yesterday':
        s.setDate(today.getDate() - 1);
        e.setDate(today.getDate() - 1);
        break;
      case '7d':
        s.setDate(today.getDate() - 6);
        break;
      case '30d':
        s.setDate(today.getDate() - 29);
        break;
      case 'thisMonth':
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth': {
        const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(firstOfThis.getTime() - 86400000);
        s = new Date(e.getFullYear(), e.getMonth(), 1);
        break;
      }
      case 'all':
        s = new Date(today.getFullYear() - 3, 0, 1);
        break;
      default:
        break;
    }
    const ns = toYmdLocal(s);
    const ne = toYmdLocal(e);
    setRange(id);
    setStartDate(ns);
    setEndDate(ne);
    fetchData(ns, ne);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableChannels = useMemo(() => {
    const set = new Set((dailyChannelRowsAll || dailyByChannelRows || []).map(r => r.sales_channel_name || 'Unknown'));
    const arr = Array.from(set);
    arr.sort();
    return ['All', ...arr];
  }, [dailyChannelRowsAll, dailyByChannelRows]);

  useEffect(() => {
    if (!weekdayChannel && availableChannels.length > 0) {
      setWeekdayChannel(availableChannels[0]);
    }
  }, [availableChannels, weekdayChannel]);

  useEffect(() => {
    try {
      const src = dailyViewAllRows || [];
      const out = [];
      for (const r of src) {
        const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
        if (!ymd) continue;
        const parts = computeIsoWeekParts(ymd);
        out.push({
          iso_year: parts.isoYear,
          iso_week: parts.isoWeek,
          iso_dow: parts.isoDow,
          day: ymd,
          orders_count: Number(r.orders_count || 0),
          items_count: Number(r.items_count || 0),
          revenue: Number(r.revenue || 0),
        });
      }
      setWeekdayByWeek(out);
    } catch (e) {
      console.error('build weekday matrix error', e);
      setWeekdayByWeek([]);
    }
  }, [dailyViewAllRows]);

  useEffect(() => {
    try {
      const src = dailyViewAllRows || [];
      if (!src || src.length === 0) { setWeeklySummary([]); return; }
      const agg = new Map();
      for (const r of src) {
        const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
        if (!ymd) continue;
        const parts = computeIsoWeekParts(ymd);
        const key = `${parts.isoYear}-${String(parts.isoWeek).padStart(2, '0')}`;
        if (!agg.has(key)) {
          agg.set(key, { iso_year: parts.isoYear, iso_week: parts.isoWeek, revenue: 0, orders_count: 0, items_count: 0 });
        }
        const row = agg.get(key);
        row.revenue += Number(r.revenue || 0);
        row.orders_count += Number(r.orders_count || 0);
        row.items_count += Number(r.items_count || 0);
      }
      const weeks = Array.from(agg.values()).sort((a, b) => a.iso_year - b.iso_year || a.iso_week - b.iso_week);
      for (let i = 0; i < weeks.length; i++) {
        const prev = i > 0 ? weeks[i - 1] : null;
        const cur = weeks[i];
        const pct = (curVal, prevVal) => (prevVal && prevVal !== 0) ? ((curVal - prevVal) / prevVal) * 100 : null;
        cur.revenue_wow_pct = pct(cur.revenue, prev?.revenue || 0);
        cur.orders_wow_pct = pct(cur.orders_count, prev?.orders_count || 0);
        cur.items_wow_pct = pct(cur.items_count, prev?.items_count || 0);
      }
      setWeeklySummary(weeks.reverse());
    } catch (e) {
      console.error('build weekly summary error', e);
      setWeeklySummary([]);
    }
  }, [dailyViewAllRows]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <ShopifySyncStatus
        rangeEmpty={loadedOnce && !loading && dailyRows.length === 0}
        onShowAll={() => applyQuickRange('all')}
        onReady={() => fetchData()}
      />
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <img 
              src="/shopify_logo_whitebg.svg"
              alt="Shopify"
              className="h-9 md:h-10 w-auto object-contain rounded-lg"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                { id: 'yesterday', label: 'Yesterday' },
                { id: '7d', label: '7 Days' },
                { id: '30d', label: '30 Days' },
                { id: 'thisMonth', label: 'This Month' },
                { id: 'lastMonth', label: 'Last Month' },
                { id: 'all', label: 'All Time' }
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    range === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                  onClick={() => applyQuickRange(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={() => fetchData()}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-blue-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-lg font-semibold mt-1">{CUR}{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-50">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-indigo-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Orders</p>
                <p className="text-lg font-semibold mt-1">{totalOrders.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-indigo-50">
                <BarChart2 className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-amber-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Items</p>
                <p className="text-lg font-semibold mt-1">{totalItems.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <Package className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-emerald-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Average Order Value</p>
                <p className="text-lg font-semibold mt-1">{CUR}{formatCurrency(aov)}</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50">
                <BarChart2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </Card>
        </div>
        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="text-sm font-medium text-gray-800">Daily Trend</div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'revenue' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('revenue')}>Revenue</button>
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'orders' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('orders')}>Orders</button>
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'items' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('items')}>Items</button>
          </div>
        </div>
        <div className="h-72">
          {lineData.length > 0 ? (
            <LineChart 
              data={{
                labels: lineData.map(d => d.label),
                datasets: [{
                  label: metric === 'revenue' ? 'Revenue' : metric === 'orders' ? 'Orders' : 'Items',
                  data: lineData.map(d => d.value),
                  borderColor: metric === 'revenue' ? 'rgb(16, 185, 129)' : metric === 'orders' ? 'rgb(37, 99, 235)' : 'rgb(217, 119, 6)',
                  backgroundColor: metric === 'revenue' ? 'rgba(16, 185, 129, 0.1)' : (metric === 'orders' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(217, 119, 6, 0.1)'),
                  fill: true,
                  tension: 0.4
                }]
              }}
            />
          ) : (
            <div className="text-sm text-gray-500">No chart data.</div>
          )}
        </div>
      </div>

      {/* Sales Channel Breakdown (All-time monthly by channel view, pivoted) */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Sales Channel Performance</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Metric</span>
            <select className="text-xs border rounded-md px-2 py-1 bg-white" value={channelMetric} onChange={(e) => setChannelMetric(e.target.value)}>
              <option value="revenue">Revenue</option>
              <option value="orders">Orders</option>
              <option value="items">Items</option>
            </select>
          </div>
        </div>
        {monthlyByChannelAll.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b align-bottom">
                  <th className="py-2 pr-4">Sales Channel</th>
                  {channelPivot.months.map((m) => (
                    <th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channelPivot.channels.map((ch) => (
                  <tr key={ch} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{ch}</td>
                    {channelPivot.months.map((m) => {
                      const v = channelPivot.valuesByChannel[ch]?.[m] || 0;
                      const txt = channelMetric === 'revenue' ? `${formatCurrency(v)}` : Number(v).toLocaleString();
                      return <td key={`${ch}-${m}`} className="py-2 pr-4">{txt}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">Weekday by ISO Week</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Channel</span>
              <select className="text-xs border rounded-md px-2 py-1 bg-white" value={weekdayChannel} onChange={(e) => setWeekdayChannel(e.target.value)}>
                {availableChannels.map((c) => (<option key={c} value={c}>{c || 'Unknown'}</option>))}
              </select>
              <span className="text-xs text-gray-500">Metric</span>
              <select className="text-xs border rounded-md px-2 py-1 bg-white" value={weeklyMetric} onChange={(e) => setWeeklyMetric(e.target.value)}>
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Week</th>
                  <th className="py-2 pr-3">Mon</th>
                  <th className="py-2 pr-3">Tue</th>
                  <th className="py-2 pr-3">Wed</th>
                  <th className="py-2 pr-3">Thu</th>
                  <th className="py-2 pr-3">Fri</th>
                  <th className="py-2 pr-3">Sat</th>
                  <th className="py-2 pr-3">Sun</th>
                  <th className="py-2 pr-3">Total</th>
                </tr>
                </thead>
                <tbody>
                {(() => {
                  const grouped = new Map();
                  for (const r of weekdayByWeek) {
                    const y = Number(r.iso_year);
                    const w = Number(r.iso_week);
                    const key = `${y}-W${String(w).padStart(2, '0')}`;
                    if (!grouped.has(key)) grouped.set(key, { year: y, week: w, byDow: Array(8).fill(0) });
                    const v = weeklyMetric === 'revenue' ? Number(r.revenue || 0)
                            : weeklyMetric === 'orders' ? Number(r.orders_count || 0)
                            : Number(r.items_count || 0);
                    grouped.get(key).byDow[Number(r.iso_dow)] = (grouped.get(key).byDow[Number(r.iso_dow)] || 0) + v;
                  }
                  const rows = Array.from(grouped.values())
                    .sort((a, b) => b.year - a.year || b.week - a.week);
                  if (rows.length === 0) return (
                    <tr><td className="py-3 text-gray-500" colSpan={9}>No data.</td></tr>
                  );
                  return rows.map((rw) => {
                    const total = rw.byDow.slice(1).reduce((s, x) => s + (x || 0), 0);
                    const fmt = (x) => weeklyMetric === 'revenue' ? `${CUR}${formatShortNumber(x)}` : formatShortNumber(x);
                    return (
                      <tr key={`${rw.year}-${rw.week}`} className="border-b">
                        <td className="py-2 pr-3 font-medium">{`W${String(rw.week).padStart(2, '0')} ${rw.year}`}</td>
                        {[1,2,3,4,5,6,7].map((d) => (
                          <td key={d} className="py-2 pr-3">{fmt(rw.byDow[d] || 0)}</td>
                        ))}
                        <td className="py-2 pr-3 font-semibold">{fmt(total)}</td>
                      </tr>
                    );
                  });
                })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-lg font-semibold">Week-over-Week Summary</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Channel</span>
              <select className="text-xs border rounded-md px-2 py-1 bg-white" value={weekdayChannel} onChange={(e) => setWeekdayChannel(e.target.value)}>
                {availableChannels.map((c) => (<option key={c} value={c}>{c || 'Unknown'}</option>))}
              </select>
              <span className="text-xs text-gray-500">Metric</span>
              <select className="text-xs border rounded-md px-2 py-1 bg-white" value={weeklyMetric} onChange={(e) => setWeeklyMetric(e.target.value)}>
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Week</th>
                  <th className="py-2 pr-3">Revenue</th>
                  <th className="py-2 pr-3">Orders</th>
                  <th className="py-2 pr-3">Items</th>
                  <th className="py-2 pr-3">Δ Rev</th>
                  <th className="py-2 pr-3">Δ Orders</th>
                  <th className="py-2 pr-3">Δ Items</th>
                </tr>
                </thead>
                <tbody>
                {weeklySummary.length === 0 ? (
                  <tr><td className="py-3 text-gray-500" colSpan={7}>No data.</td></tr>
                ) : weeklySummary.map((r, idx) => {
                  const wLabel = `W${String(r.iso_week).padStart(2, '0')} ${r.iso_year}`;
                  const fmtC = (v) => `${CUR}${formatShortNumber(v)}`;
                  const fmtN = (v) => formatShortNumber(v);
                  const fmtPct = (v) => (v === null || v === undefined) ? '-' : `${(Number(v)).toFixed(1)}%`;
                  const color = (v) => (v === null || v === undefined) ? 'text-gray-500' : (v >= 0 ? 'text-emerald-600' : 'text-rose-600');
                  const hi = (col) => weeklyMetric === col ? 'font-semibold text-gray-900' : 'text-gray-700';
                  return (
                    <tr key={idx} className="border-b">
                      <td className="py-2 pr-3 font-medium">{wLabel}</td>
                      <td className={`py-2 pr-3 ${hi('revenue')}`}>{fmtC(r.revenue || 0)}</td>
                      <td className={`py-2 pr-3 ${hi('orders')}`}>{fmtN(r.orders_count || 0)}</td>
                      <td className={`py-2 pr-3 ${hi('items')}`}>{fmtN(r.items_count || 0)}</td>
                      <td className={`py-2 pr-3 ${color(r.revenue_wow_pct)}`}>{fmtPct(r.revenue_wow_pct)}</td>
                      <td className={`py-2 pr-3 ${color(r.orders_wow_pct)}`}>{fmtPct(r.orders_wow_pct)}</td>
                      <td className={`py-2 pr-3 ${color(r.items_wow_pct)}`}>{fmtPct(r.items_wow_pct)}</td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


